import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Button, Card, Col, Divider, Form, Image, Input, Layout, message, Row, Switch, Typography } from 'antd'
import { Link, useHistory } from 'react-router-dom'
import SpinLoader from '../../helpers/SpinLoader'
import { useCallContext } from '../../../context/call/CallProvider'
import { useAuthContext } from '../../../context/auth'
import { MessageType } from 'antd/lib/message'
import { currentBrand } from '../../../config'
import logo from '../../../images/icons/logo.png'
import ErrorMessage from '../../helpers/ErrorMessage'
import { customSessionStorage } from '../../../context/customStorage'

import './lobby.less'
import PermissionSettings from './PermissionSettings'
import { TMediaConstraints } from '../../../context/types'

const { Content } = Layout
const { Text, Paragraph, Title, Link: AntLink } = Typography

type TParams = {
	id: string
}

type TConnectRequest = {
	first_name: string
	last_name: string
	email: string
}

function Lobby({ id }: TParams) {
	const [form] = Form.useForm()
	const { call, actions } = useCallContext()
	const { auth } = useAuthContext()
	const history = useHistory()

	const [showAuth, setShowAuth] = useState<boolean>(null)
	const [userData, setUserData] = useState<TConnectRequest>(null)
	const [formErrors, setFormErrors] = useState([])

	useEffect(() => {
		actions.getCallById(id)
		return () => actions.clearCallData()
	}, [])

	useEffect(() => {
		if (!auth.user) {
			setShowAuth(true)
		} else {
			setShowAuth(false)
		}
	}, [auth.user])

	useEffect(() => {
		if (call.error && auth.user) {
			history.push('/profile/calls')
			message.error(
				{
					content: <ErrorMessage key="joincallerror" errorMsg={call.error || 'Something wrong'} />,
					key: 'joincallerror',
				},
				5
			)
		}
	}, [call.error])

	useEffect(() => {
		let hideMessage: MessageType | undefined
		const messageText = call.callMessage || 'Joining'
		if (call.loading) {
			const content = (
				<Text>
					{`${messageText} `}
					<Button type="link" onClick={actions.disconnect}>
						Disconnect
					</Button>
				</Text>
			)
			hideMessage = message.loading({
				content,
				duration: 0,
				key: 'joinexpert',
			})
		} else if (hideMessage) {
			hideMessage()
		}
		return () => {
			if (hideMessage) {
				hideMessage()
			}
		}
	}, [call.loading, call.callToken, call.call])

	const initialValues = useMemo(
		() => ({
			first_name: auth.user?.first_name,
			last_name: auth.user?.last_name,
		}),
		[auth.user?.first_name, auth.user?.last_name]
	)

	const cardTitle = useMemo(() => {
		return (
			<>
				<AntLink href={currentBrand.landingUrl || '/'} className="header-logo-link call-logo-link">
					<Image src={currentBrand.logoUrl || logo} preview={false} referrerPolicy="no-referrer" />
				</AntLink>
				<Divider />
				<Title level={3} style={{ fontSize: 24 }}>
					{call.loading && (
						<strong style={{ whiteSpace: 'normal' }}>
							{`${call.callData?.user?.first_name} ${call.callData?.user?.last_name}`} &#8594;{' '}
							<Link
								to={'/expert/' + call.callData?.expert?.slug}
								target="_blank"
							>{`${call.callData?.expert?.profile?.user.first_name} ${call.callData?.expert?.profile.user.last_name}`}</Link>
						</strong>
					)}
				</Title>
			</>
		)
	}, [call.callData])

	const onFinish = (values: TConnectRequest) => {
		setUserData({
			first_name: values.first_name,
			last_name: values.last_name,
			email: values.email,
		})
		setShowAuth(false)
	}

	const onFieldsChange = () => {
		const nextFormErrors = form.getFieldsError().filter((v) => v.errors.length)
		setFormErrors(nextFormErrors)
	}

	const handleGoToLogin = () => {
		customSessionStorage.setItem('redirectUrl', window.location.pathname)
	}

	function connectToCall(constraints: TMediaConstraints) {
		if (auth.user?.id === call.callData?.expert?.profile?.user?.id) {
			actions.joinExpertCall(id, { media: constraints })
		} else {
			const data = {
				first_name: userData ? userData.first_name : auth.user.first_name,
				last_name: userData ? userData.last_name : auth.user.last_name,
				email: userData ? userData.email : auth.user.email,
			}
			actions.joinCall(id, {
				...data,
				media: constraints,
			})
		}
	}

	return (
		<Layout className="lobby">
			<Content className="lobby-content">
				<SpinLoader loading={!!call.loading && !call.loading} className="lobby-wrapper">
					{showAuth && (
						<Form
							name="CallForm"
							form={form}
							initialValues={initialValues}
							onFinish={onFinish}
							onFieldsChange={onFieldsChange}
							scrollToFirstError
							layout="vertical"
						>
							<Card title={cardTitle} headStyle={{ border: 'none' }} className="lobby-card">
								<Paragraph>
									Are you already a member?{' '}
									<Button
										shape="round"
										type="primary"
										htmlType="button"
										href={'/login'}
										onClick={handleGoToLogin}
										style={{ color: '#fff', marginLeft: 10 }}
									>
										Login to join call
									</Button>
								</Paragraph>
								<Paragraph>
									Not a member?{' '}
									<Button
										shape="round"
										type="primary"
										htmlType="button"
										href={'/register'}
										onClick={handleGoToLogin}
										style={{ color: '#fff', marginLeft: 10 }}
									>
										Register here
									</Button>
								</Paragraph>
								<Paragraph style={{ margin: '28px 0' }}>
									<Text strong>
										<p>Or enter with your name</p>
									</Text>
								</Paragraph>
								<Form.Item
									label="First Name"
									name="first_name"
									rules={[{ required: true, message: 'Please input your First Name!' }]}
								>
									<Input disabled={!!call.loading} />
								</Form.Item>
								<Form.Item
									label="Last Name"
									name="last_name"
									rules={[{ required: true, message: 'Please input your Last Name!' }]}
								>
									<Input disabled={!!call.loading} />
								</Form.Item>
								<Form.Item
									label="Email"
									name="email"
									rules={[{ type: 'email', required: true, message: 'Please input your Email!' }]}
								>
									<Input disabled={!!call.loading} />
								</Form.Item>
								<Row>
									<Col span={12}>
										<Form.Item name="videoMuted" label="Turn On Camera">
											<Switch disabled={!!call.loading} checked={!call.videoMuted} onChange={actions.videoMuteToggle} />
										</Form.Item>
									</Col>
									<Col span={12}>
										<Form.Item name="audioMuted" label="Turn On Audio">
											<Switch disabled={!!call.loading} checked={!call.audioMuted} onChange={actions.audioMuteToggle} />
										</Form.Item>
									</Col>
								</Row>
								<Alert
									message="Reminder"
									showIcon
									description="All calls are recorded for quality assurance."
									type="info"
									style={{ margin: '20px' + ' 0' }}
								/>
								{call.error && (
									<Alert showIcon message="Error" description={call.error} type="error" style={{ margin: '20px 0' }} />
								)}
								{formErrors.length > 0 && (
									<Alert
										showIcon
										type="warning"
										message="Warning"
										style={{ margin: '20px 0' }}
										description={formErrors.map((v) => (
											<Paragraph key={v.name + '_errors'} style={{ marginBottom: 0 }}>
												{v.errors[0]}
											</Paragraph>
										))}
									/>
								)}
								<Button
									shape="round"
									type="primary"
									htmlType="submit"
									loading={!!call.loading}
									style={{ marginRight: 'auto' }}
								>
									Enter room
								</Button>
							</Card>
						</Form>
					)}
					{!call.loading && showAuth === false && <PermissionSettings connectToCall={connectToCall} />}
				</SpinLoader>
			</Content>
		</Layout>
	)
}

export default Lobby
